import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import "./EcommerceFavoritesSection.scss";
import { useAppContextNonNull } from "@contexts/AppContext";
import { useFavorites } from "@contexts/FavoritesContext";
import FavoritesService, { FAVORITES_MAX_PAGE_SIZE } from "@services/FavoritesService";
import UserService from "@services/UserService";
import ICatalog_Item from "@models/ICatalog_Item";
import IOrder from "@models/IOrder";
import IConnection_Token from "@models/IConnection_Token";
import EcommerceViewModeEnum from "@models/EcommerceViewModeEnum";
import { ProcessOrderEntryItemFn } from "@models/OrderEntryTypes";
import EcommerceListView from "../../order_entry/EcommerceListView/EcommerceListView";
import { EcommerceListViewSkeleton } from "../EcommerceListViewSkeleton/EcommerceListViewSkeleton";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { OwP } from "../../shared/OwP/OwP";

type Props = {
    viewMode: EcommerceViewModeEnum;
    addToCart: ProcessOrderEntryItemFn;
    openDetailView: (item: ICatalog_Item) => void;
    supplierVendorToken?: IConnection_Token;
    /** The guids currently rendered here, so the host can de-duplicate the catalog grid below. */
    onVisibleFavoritesChange?: (guids: Set<string>) => void;
    onViewFullCatalog: () => void;
    onBuildFromRecent: () => void;
};

/**
 * Customer home "Favorites" section: the account's reorder list (GET /v1/favorites, newest first,
 * availability-filtered server-side) rendered with the same cards as the catalog. Refetches whenever
 * the favorites context reports a change, so toggles and bulk adds show up immediately.
 */
export const EcommerceFavoritesSection: React.FC<Props> = (props) => {
    const { vendorModel, supplierModel, socket, userModel } = useAppContextNonNull();
    const favorites = useFavorites();

    const [items, setItems] = useState<ICatalog_Item[]>([]);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    // Drops a stale response when a newer version has been requested.
    const requestIdRef = useRef(0);

    const { onVisibleFavoritesChange } = props;

    useEffect(() => {
        if (!favorites.enabled) {
            return;
        }

        const requestId = ++requestIdRef.current;

        setIsLoading(true);
        setLoadError(false);

        FavoritesService.getFavorites({ limit: FAVORITES_MAX_PAGE_SIZE, offset: 0 }, userModel, supplierModel, vendorModel)
            .then((result) => {
                if (requestId !== requestIdRef.current) {
                    return;
                }

                setItems(result.catalog_items || []);
                setTotal(result.total || 0);
                setHasMore(!!result.has_more);
            })
            .catch((error) => {
                console.error("Failed to load favorites", error);

                if (requestId === requestIdRef.current) {
                    setLoadError(true);
                }
            })
            .finally(() => {
                if (requestId === requestIdRef.current) {
                    setIsLoading(false);
                }
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favorites.enabled, favorites.version, userModel?.guid, supplierModel?.guid, vendorModel?.guid]);

    // An unfavorited card leaves the section right away (optimistic) rather than after the refetch.
    const visibleItems = items.filter((item) => favorites.isFavorite(item.guid));

    const visibleKey = visibleItems.map((item) => item.guid).join("|");

    useEffect(() => {
        onVisibleFavoritesChange?.(new Set(visibleItems.map((item) => item.guid)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleKey, onVisibleFavoritesChange]);

    useEffect(() => {
        return () => {
            onVisibleFavoritesChange?.(new Set());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!favorites.enabled) {
        return null;
    }

    const isListMode = props.viewMode === EcommerceViewModeEnum.LIST;
    const gridClass = isListMode ? "ecomm-list" : "ecomm-grid";
    const supplierVendorToken = props.supplierVendorToken ?? UserService.getSupplierVendorToken(vendorModel, supplierModel.id);

    const renderBody = () => {
        if (isLoading && !items.length) {
            return (
                <div className={gridClass}>
                    {Array.from({ length: 4 }).map((_, i) => (
                        <EcommerceListViewSkeleton key={`favorites-skeleton-${i}`} />
                    ))}
                </div>
            );
        }

        if (loadError) {
            return (
                <div className={"favorites-empty"}>
                    <OwP content={"favorites_load_failed"} />
                </div>
            );
        }

        if (!visibleItems.length) {
            return (
                <div className={"favorites-empty"} data-testid={"favorites-empty-state"}>
                    <i className={"far fa-heart favorites-empty-icon"} aria-hidden={true} />
                    <OwLabel title={"favorites_empty_title"} isBold={true} className={"d-block mb-1"} />
                    <OwP content={"favorites_empty_prompt"} className={"text-muted"} />
                    <div className={"favorites-empty-actions"}>
                        <OwButton
                            className={"btn-primary"}
                            title={"build_from_recent_orders"}
                            leftIconClass={"fas fa-history"}
                            fn={props.onBuildFromRecent}
                            testId={"build-from-recent-orders"}
                        />
                        <OwButton
                            className={"btn-outline-secondary"}
                            title={"view_full_catalog"}
                            fn={props.onViewFullCatalog}
                            testId={"favorites-empty-view-full-catalog"}
                        />
                    </div>
                </div>
            );
        }

        return (
            <>
                <div className={gridClass}>
                    {visibleItems.map((item) => (
                        <EcommerceListView
                            key={item.guid}
                            catalogItem={item}
                            priceGroupName={vendorModel.Price_Group?.value || ""}
                            addToCart={props.addToCart}
                            openDetailView={props.openDetailView}
                            isReadOnly={false}
                            order={{ Price_Group: vendorModel.Price_Group } as IOrder}
                            supplierVendorToken={supplierVendorToken}
                            socket={socket}
                            isCategoryView={false}
                            viewMode={props.viewMode}
                        />
                    ))}
                </div>
                {hasMore ? (
                    <div className={"text-muted text-small mt-2"}>
                        <FormattedMessage id={"showing_first_favorites"} values={{ count: items.length, total }} />
                    </div>
                ) : null}
            </>
        );
    };

    return (
        <section className={"EcommerceFavoritesSection"} aria-labelledby={"ecomm-favorites-heading"} data-testid={"favorites-section"}>
            <div className={"favorites-header"}>
                <h2 className={"favorites-title"} id={"ecomm-favorites-heading"}>
                    <i className={"fas fa-heart me-2"} aria-hidden={true} />
                    <FormattedMessage id={"favorites"} />
                    {visibleItems.length ? <span className={"favorites-count"}>({visibleItems.length})</span> : null}
                </h2>
                <div className={"favorites-header-actions"}>
                    {visibleItems.length ? (
                        <button type={"button"} className={"btn btn-link btn-sm p-0"} onClick={props.onBuildFromRecent}>
                            <i className={"fas fa-history me-1"} aria-hidden={true} />
                            <FormattedMessage id={"build_from_recent_orders"} />
                        </button>
                    ) : null}
                    <button
                        type={"button"}
                        className={"btn btn-link btn-sm p-0"}
                        onClick={props.onViewFullCatalog}
                        data-testid={"view-full-catalog"}>
                        <FormattedMessage id={"view_full_catalog"} />
                        <i className={"fas fa-arrow-down ms-1"} aria-hidden={true} />
                    </button>
                </div>
            </div>
            {renderBody()}
        </section>
    );
};

export default EcommerceFavoritesSection;
